/**
 * CapacityWidget — today's workload vs available capacity per mechanic.
 * Available hours come from the shift model; committed hours from assigned
 * bookings' estimates. When no shifts are configured, prompts the admin to set
 * them up (available hours would otherwise be meaningless).
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { chartTooltipProps } from '@/lib/chartTheme';
import { EmptyState } from '@/components/common/EmptyState';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import type { CapacitySummary } from '@/hooks/useDashboard';

interface CapacityWidgetProps {
  data?: CapacitySummary | null;
}

export function CapacityWidget({ data }: CapacityWidgetProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const theme = useTheme();

  const mechanics = data?.mechanics ?? [];
  const notConfigured = !data || !data.shiftsConfigured;

  if (notConfigured || mechanics.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('capacity.title')}
          </Typography>
          {notConfigured ? (
            <EmptyState
              icon={<EventBusyIcon />}
              title={t('capacity.notConfigured')}
              description={t('capacity.notConfiguredDesc')}
              minHeight={200}
              action={{
                label: t('capacity.configureShifts'),
                onClick: () => navigate('/hr?tab=shift-patterns'),
                variant: 'outlined',
              }}
            />
          ) : (
            <EmptyState icon={<EventBusyIcon />} title={t('capacity.empty')} minHeight={200} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('capacity.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('today')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight={600} color="primary.main">
              {data.totalCommittedHours}h / {data.totalAvailableHours}h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('capacity.committedVsAvailable')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mechanics} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}h`}
              />
              <Tooltip
                {...chartTooltipProps(theme)}
                formatter={(value: number, name: string) => [`${value}h`, name]}
              />
              <Legend iconType="circle" iconSize={10} />
              <Bar
                dataKey="availableHours"
                name={t('capacity.available')}
                fill={theme.palette.primary.light}
                radius={[4, 4, 0, 0]}
                barSize={16}
              />
              <Bar
                dataKey="committedHours"
                name={t('capacity.committed')}
                fill={theme.palette.warning.dark}
                radius={[4, 4, 0, 0]}
                barSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
